import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { OptionCard } from './OptionCard';
import { getBusinessPosRoles } from '../api/client';

/** Default roles when the business's real roles can't be fetched (these two always exist per business). */
const DEFAULT_ROLES = ['Cashier', 'Admin'];

/**
 * POS-role selector for the create-user modals (#37 Phase-B). Fetches the business's REAL roles
 * from its POS DB and renders them; falls back to a default set if none/unreachable. Keeps `value`
 * valid by auto-selecting the first role once the list loads.
 */
export function PosRoleSelector({
  businessId,
  value,
  onChange,
}: {
  businessId: string;
  value: string;
  onChange: (role: string) => void;
}) {
  const { data } = useQuery({
    queryKey: ['pos-roles', businessId],
    queryFn: () => getBusinessPosRoles(businessId),
    enabled: !!businessId,
    staleTime: 60_000,
  });
  const roles = data && data.length ? data : DEFAULT_ROLES;

  // Once the real roles load, snap the selection to a valid one (the fixed default may not exist).
  useEffect(() => {
    if (roles.length && !roles.includes(value)) onChange(roles[0]!);
  }, [roles, value, onChange]);

  return (
    <div>
      <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
        POS role <span className="text-rose-600">*</span>
      </span>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {roles.map((r) => (
          <OptionCard key={r} selected={value === r} onSelect={() => onChange(r)} className="text-center py-3">
            <span className="text-sm font-medium text-zinc-950 dark:text-zinc-50">{r}</span>
          </OptionCard>
        ))}
      </div>
      <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
        This business's POS roles. The user gets the matching <span className="mono">Role#id</span> role in the POS.
      </p>
    </div>
  );
}
